/*
 * Lautio scroll scene runtime: one rendered move, driven by the reader's scroll.
 *
 * Every frame is a complete render of one scene from one camera. Native scroll
 * drives a target progress; a rendered progress follows it through a critically
 * damped spring integrated against real elapsed time, so the motion is
 * frame-rate independent, reversible along the same path and interruptible.
 *
 * What is on the stage, bottom to top:
 *
 *   the poster      an <img> in the markup: the whole experience without JS,
 *                   without video and under reduced motion
 *   the motion      a canvas. The move is an H.264 file fetched whole, and
 *   canvas          decoded one pose at a time by WebCodecs: the file is
 *                   demuxed here, every pose is one encoded chunk, and a
 *                   VideoDecoder hands back frames a few milliseconds after
 *                   they are asked for - no media pipeline, no seek, no
 *                   presentation callback to wait on. The poses the reader is
 *                   heading for are decoded ahead and held, so at a reading
 *                   pace a pose change is one drawImage of a frame already in
 *                   hand. Where WebCodecs is missing the same file is decoded
 *                   by a few hidden <video> elements that take turns being
 *                   seeked (the 2026-09-10 runtime, kept whole below). Either
 *                   way a pose is drawn into the canvas only once its frame is
 *                   really there, and the canvas keeps its pixels between
 *                   draws, so nothing on the stage can ever be blank. No video
 *                   element is ever shown: a video layer that is seeked, or
 *                   made visible, is where iOS shows a black frame.
 *   the sharp       a second canvas holding the sharp render (2560 wide) of
 *   canvas          the pose the reader is resting on, drawn once when they
 *                   stop and faded in over the motion; hidden the instant
 *                   they move again.
 *
 * Two video tiers: a small preview (960 or 540 wide, under a megabyte) that is
 * in before the reader can have scrolled to the chapter, and the full one that
 * replaces it pose for pose when it lands. Until any video is in, a desktop
 * draws sharp frames straight into the sharp canvas as it always did.
 *
 * Deliberately absent: scrolljacking, wheel handlers, any 1:1 binding of a
 * wheel event to a transform.
 */
(() => {
  'use strict';

  const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

  // ------------------------------------------------------------- WebCodecs
  // The move is an MP4 with one H.264 sample per pose. The sample table is
  // read here - it is a few hundred bytes at the front of the file - so each
  // pose can be handed to a VideoDecoder as one chunk.
  const box4 = (u8, p) => String.fromCharCode(u8[p], u8[p + 1], u8[p + 2], u8[p + 3]);

  const walkBoxes = (u8, dv, start, end, visit) => {
    let p = start;
    while (p + 8 <= end) {
      let size = dv.getUint32(p);
      const type = box4(u8, p + 4);
      let hdr = 8;
      if (size === 1) { size = Number(dv.getBigUint64(p + 8)); hdr = 16; } else if (size === 0) size = end - p;
      if (size < hdr || p + size > end) break;
      visit(type, p + hdr, p + size);
      p += size;
    }
  };

  function demuxMp4(buffer) {
    const u8 = new Uint8Array(buffer);
    const dv = new DataView(buffer);
    let found = null;
    const parseStsd = (a, b) => {
      const n = dv.getUint32(a + 4);
      let p = a + 8;
      for (let i = 0; i < n && p + 8 <= b; i += 1) {
        const size = dv.getUint32(p);
        const type = box4(u8, p + 4);
        if (type === 'avc1' || type === 'avc3') {
          // VisualSampleEntry: 78 bytes of fields, then the child boxes
          const width = dv.getUint16(p + 8 + 24);
          const height = dv.getUint16(p + 8 + 26);
          let avcC = null;
          walkBoxes(u8, dv, p + 8 + 78, p + size, (t, x, y) => { if (t === 'avcC') avcC = u8.subarray(x, y); });
          if (!avcC || avcC.length < 4) return null;
          const hex = (v) => v.toString(16).padStart(2, '0');
          return { codec: `avc1.${hex(avcC[1])}${hex(avcC[2])}${hex(avcC[3])}`, description: avcC, width, height };
        }
        p += size;
      }
      return null;
    };
    const containers = { moov: 1, trak: 1, mdia: 1, minf: 1 };
    const walk = (s, e) => walkBoxes(u8, dv, s, e, (t, a, b) => {
      if (found) return;
      if (containers[t]) { walk(a, b); return; }
      if (t !== 'stbl') return;
      const tables = {};
      walkBoxes(u8, dv, a, b, (tt, x, y) => { tables[tt] = [x, y]; });
      if (!tables.stsd || !tables.stsz || !tables.stsc || !(tables.stco || tables.co64)) return;
      const entry = parseStsd(tables.stsd[0], tables.stsd[1]);
      if (entry) found = { entry, tables };
    });
    walk(0, u8.length);
    if (!found) return null;
    const { entry, tables } = found;
    const za = tables.stsz[0];
    const fixed = dv.getUint32(za + 4);
    const count = dv.getUint32(za + 8);
    const sizes = new Array(count);
    for (let i = 0; i < count; i += 1) sizes[i] = fixed || dv.getUint32(za + 12 + i * 4);
    const ca = tables.stsc[0];
    const runs = [];
    const nr = dv.getUint32(ca + 4);
    for (let i = 0; i < nr; i += 1) runs.push({ first: dv.getUint32(ca + 8 + i * 12), per: dv.getUint32(ca + 12 + i * 12) });
    const offsets = [];
    if (tables.co64) {
      const oa = tables.co64[0];
      const no = dv.getUint32(oa + 4);
      for (let i = 0; i < no; i += 1) offsets.push(Number(dv.getBigUint64(oa + 8 + i * 8)));
    } else {
      const oa = tables.stco[0];
      const no = dv.getUint32(oa + 4);
      for (let i = 0; i < no; i += 1) offsets.push(dv.getUint32(oa + 8 + i * 4));
    }
    const samples = [];
    let r = 0;
    for (let c = 0; c < offsets.length && samples.length < count && runs.length; c += 1) {
      while (r + 1 < runs.length && runs[r + 1].first <= c + 1) r += 1;
      let off = offsets[c];
      for (let k = 0; k < runs[r].per && samples.length < count; k += 1) {
        const i = samples.length;
        samples.push({ offset: off, size: sizes[i], key: !tables.stss });
        off += sizes[i];
      }
    }
    if (tables.stss) {
      const sa = tables.stss[0];
      const ns = dv.getUint32(sa + 4);
      for (let i = 0; i < ns; i += 1) { const s = dv.getUint32(sa + 8 + i * 4) - 1; if (samples[s]) samples[s].key = true; }
    }
    if (!samples.length || !samples[0].key) return null;
    return { codec: entry.codec, description: entry.description, width: entry.width, height: entry.height, samples, u8 };
  }

  // Work that is not for this frame - tearing a decoder down, dropping a
  // tier - waits for a pause in the scrolling, so its cost never lands in a
  // move. One listener for the page; the poll is a timer, not a frame.
  let lastScroll = 0;
  let scrollWatched = false;
  const isStill = () => {
    if (!scrollWatched) {
      scrollWatched = true;
      lastScroll = performance.now();
      window.addEventListener('scroll', () => { lastScroll = performance.now(); }, { passive: true });
    }
    return performance.now() - lastScroll > 320;
  };
  const whenStill = (fn) => {
    const look = () => { if (isStill()) fn(); else setTimeout(look, 150); };
    setTimeout(look, 0);
  };

  const codecSupport = new Map();
  function decoderSupported(demuxed) {
    if (typeof VideoDecoder !== 'function' || typeof EncodedVideoChunk !== 'function') return Promise.resolve(false);
    const key = `${demuxed.codec}/${demuxed.width}x${demuxed.height}`;
    if (!codecSupport.has(key)) {
      let p;
      try {
        p = VideoDecoder.isConfigSupported({ codec: demuxed.codec, description: demuxed.description, codedWidth: demuxed.width, codedHeight: demuxed.height, optimizeForLatency: true })
          .then((r) => !!(r && r.supported)).catch(() => false);
      } catch (e) { p = Promise.resolve(false); }
      codecSupport.set(key, p);
    }
    return codecSupport.get(key);
  }

  // One decoder per tier. It holds a window of decoded poses around the one
  // the reader is on - ahead in the direction of travel, a few behind for a
  // reversal - and decodes towards the poses the reader is heading for, so
  // at a reading pace the next pose is already in hand when it is asked for.
  class PoseDecoder {
    constructor(demuxed, hooks) {
      this.d = demuxed;
      this.n = demuxed.samples.length;
      this.hooks = hooks;
      this.frames = new Map();       // index -> VideoFrame
      this.pending = new Set();      // submitted, not out yet
      this.submitAt = new Map();
      this.pos = -1;                 // the last sample fed; -1 means the next must be a key
      this.wanted = 0;
      this.dir = 1;
      this.broken = false;
      this.flushing = false;
      this.timer = 0;
      this.lastSubmit = 0;
      this.lastOutput = 0;
      this.outputs = 0;
      this.flushes = 0;
      this.decodeTimes = [];         // submit to output, ms, for the proofs
      // the window is a memory budget: a decoded 1242 x 2484 frame is 4.6 MB
      const bytes = demuxed.width * demuxed.height * 1.5;
      const keep = clamp(Math.floor(64e6 / bytes), 8, 40);
      this.ahead = clamp(Math.round(keep * 0.7), 6, 28);
      this.behind = Math.max(2, keep - this.ahead);
      this.maxQueue = 10;
      const t0 = performance.now();
      this.decoder = new VideoDecoder({ output: (f) => this.onOutput(f), error: (e) => this.onError(e) });
      this.decoder.configure({ codec: demuxed.codec, description: demuxed.description, codedWidth: demuxed.width, codedHeight: demuxed.height, optimizeForLatency: true });
      this.setupMs = performance.now() - t0;   // what configuring cost the main thread
      mark('decoder-configure', t0);
    }

    get(i) { return this.frames.get(i) || null; }

    // a decoded pose past the shown one and no further than the wanted one,
    // the nearest to the wanted
    nearest(index, shown, dir) {
      let best = -1;
      for (const i of this.frames.keys()) {
        if (shown >= 0 && (i - shown) * dir <= 0) continue;
        if ((index - i) * dir < 0) continue;
        if (best < 0 || Math.abs(index - i) < Math.abs(index - best)) best = i;
      }
      return best;
    }

    want(index, dir) {
      if (this.broken) return;
      this.parked = false;
      this.wanted = index;
      this.dir = dir || 1;
      this.evict();
      this.pump();
      this.watchdog();
    }

    evict() {
      for (const [i, f] of this.frames) {
        const rel = (i - this.wanted) * this.dir;
        if (rel > this.ahead || rel < -this.behind) { f.close(); this.frames.delete(i); }
      }
    }

    keyBefore(i) {
      const s = this.d.samples;
      for (let k = i; k >= 0; k -= 1) if (s[k].key) return k;
      return 0;
    }

    // the poses to have in hand, nearest first
    targets() {
      const out = [];
      const push = (i) => { if (i >= 0 && i < this.n && !this.frames.has(i) && !this.pending.has(i)) out.push(i); };
      push(this.wanted);
      for (let d = 1; d <= this.ahead; d += 1) push(this.wanted + d * this.dir);
      push(this.wanted - this.dir);
      return out;
    }

    pump() {
      if (this.broken || this.flushing || this.decoder.state !== 'configured') return;
      for (const i of this.targets()) {
        if (this.decoder.decodeQueueSize + this.pending.size >= this.maxQueue) break;
        // carry on from where the stream is when that is the shorter road,
        // otherwise start again at the keyframe before the pose
        const key = this.keyBefore(i);
        const cont = this.pos >= 0 && i > this.pos && (i - this.pos) <= (i - key + 1);
        for (let k = cont ? this.pos + 1 : key; k <= i; k += 1) if (!this.feed(k)) return;
      }
    }

    feed(k) {
      const s = this.d.samples[k];
      let chunk;
      try {
        chunk = new EncodedVideoChunk({ type: s.key ? 'key' : 'delta', timestamp: k * 1000, duration: 1000, data: this.d.u8.subarray(s.offset, s.offset + s.size) });
        this.decoder.decode(chunk);
      } catch (e) {
        this.onError(e);
        return false;
      }
      const now = performance.now();
      this.pending.add(k);
      this.submitAt.set(k, now);
      this.lastSubmit = now;
      this.pos = k;
      return true;
    }

    onOutput(frame) {
      const i = Math.round(frame.timestamp / 1000);
      const now = performance.now();
      this.pending.delete(i);
      const at = this.submitAt.get(i);
      if (at !== undefined) {
        this.submitAt.delete(i);
        this.decodeTimes.push(now - at);
        if (this.decodeTimes.length > 240) this.decodeTimes.shift();
      }
      this.lastOutput = now;
      this.outputs += 1;
      const rel = (i - this.wanted) * this.dir;
      if (this.broken || rel > this.ahead || rel < -this.behind) { frame.close(); return; }
      const old = this.frames.get(i);
      if (old) old.close();
      this.frames.set(i, frame);
      this.hooks.onFrame(i);
      this.pump();
    }

    // An engine that holds its output until it is flushed (or until the next
    // chunk lands) would leave a wanted pose in the decoder for good: a pose
    // submitted with nothing out since is drained. After a flush the stream
    // starts again at a keyframe.
    watchdog() {
      if (this.timer) return;
      this.timer = setTimeout(() => {
        this.timer = 0;
        if (this.broken || this.flushing || !this.pending.size) return;
        const now = performance.now();
        if (now - this.lastSubmit > 60 && now - this.lastOutput > 60) this.drain();
        else this.watchdog();
      }, 70);
    }

    drain() {
      if (this.flushing || this.decoder.state !== 'configured') return;
      this.flushing = true;
      this.flushes += 1;
      const done = () => { this.flushing = false; this.pos = -1; this.pending.clear(); this.submitAt.clear(); this.pump(); };
      this.decoder.flush().then(done, (e) => { done(); if (this.decoder.state === 'closed') this.onError(e); });
    }

    // Off screen: the frames go (they are the memory), the decoder and the
    // file stay, so the chapter comes back without a fetch, a demux or a
    // configure - and without the teardown, which WebKit charges to the
    // main thread later, in the middle of the next chapter's scroll.
    park() {
      if (this.broken) return;
      if (this.timer) { clearTimeout(this.timer); this.timer = 0; }
      for (const f of this.frames.values()) f.close();
      this.frames.clear();
      this.parked = true;
      this.pos = -1;
      try { if (this.decoder.state === 'configured' && !this.flushing) { this.decoder.reset(); this.decoder.configure({ codec: this.d.codec, description: this.d.description, codedWidth: this.d.width, codedHeight: this.d.height, optimizeForLatency: true }); } } catch (e) { this.onError(e); }
      this.pending.clear();
      this.submitAt.clear();
    }

    latency() {
      const n = this.decodeTimes.length;
      if (n < 4) return 8;
      const a = this.decodeTimes.slice(-24).sort((x, y) => x - y);
      return a[Math.floor(a.length / 2)];
    }

    onError(e) {
      if (this.broken) return;
      this.broken = true;
      this.hooks.onError(e);
    }

    destroy() {
      const t0 = performance.now();
      this.broken = true;
      if (this.timer) { clearTimeout(this.timer); this.timer = 0; }
      for (const f of this.frames.values()) f.close();
      this.frames.clear();
      this.pending.clear();
      this.submitAt.clear();
      try { if (this.decoder.state !== 'closed') this.decoder.close(); } catch (e) { /* already closed */ }
      mark('decoder-destroy', t0);
    }
  }

  // main-thread costs of the one-off work, readable by the proofs as
  // performance measures: what a hitch during a scroll was spent on
  const mark = (name, t0) => {
    try { performance.measure(`lautio:${name}`, { start: t0, end: performance.now() }); } catch (e) { /* no measure */ }
  };

  class RackMotion {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('.lautio-rack__stage') || root;
      this.canvas = root.querySelector('[data-lautio-canvas]');
      this.track = root.querySelector('[data-lautio-track]');
      this.status = root.querySelector('[data-lautio-status]');
      this.progress = root.querySelector('[data-lautio-progress]');
      this.ctx = this.canvas.getContext('2d', { alpha: false });
      // the motion canvas sits under the sharp one, over the poster
      this.motion = document.createElement('canvas');
      this.motion.className = 'lautio-rack__motion';
      this.motion.setAttribute('aria-hidden', 'true');
      this.stage.insertBefore(this.motion, this.canvas);
      this.mctx = this.motion.getContext('2d', { alpha: false });
      // Two buffers the size of the motion canvas. A pose is copied out of its
      // decoder once, into a buffer; a blend then only composites two buffers,
      // which the GPU does for nothing. Copying the video itself on every
      // refresh of a blend was what left Safari seven poses behind the scroll.
      this.bufA = document.createElement('canvas');
      this.bufB = document.createElement('canvas');
      this.bufFront = this.bufA;
      this.bufBack = this.bufB;
      this.hasBack = false;
      // a 16 x 16 thumbnail of each copied frame, read back to make sure the
      // decoder handed over a picture and not an empty (black) frame
      this.probe = document.createElement('canvas');
      this.probe.width = 16; this.probe.height = 16;
      this.probeCtx = this.probe.getContext('2d', { alpha: false, willReadFrequently: true });

      this.frames = [];
      this.resident = [];
      this.inflight = new Map();
      this.generation = 0;     // the video tiers: bumped on release and on an orientation change
      this.frameGen = 0;       // the sharp frames: bumped whenever they are closed
      this.ready = false;
      this.raf = 0;
      this.lastT = 0;

      this.target = 0;         // where the scroll says we should be
      this.rendered = 0;       // where we actually are
      this.velocity = 0;       // progress units per second
      this.drawnIndex = -1;    // the sharp frame on the sharp canvas
      this.wantedIndex = -1;
      this.motionIndex = -1;   // the pose on the motion canvas

      const small = Math.min(window.innerWidth, window.innerHeight) < 820;
      this.omega = Number(root.dataset.omega || 9);
      // a finger expects the picture right under it: a phone follows closer
      if (small && !root.dataset.omega) this.omega = 13;
      this.maxVelocity = Number(root.dataset.maxVelocity || 2.4);

      this.frameCount = Number(root.dataset.frameCount || 48);
      // the video may carry more poses than there are sharp frames (a denser
      // motion render); the sharp frame k is video pose k * (vf-1)/(n-1)
      this.videoFramesLandscape = Number(root.dataset.videoFrames || 0) || this.frameCount;
      this.videoFramesPortrait = Number(root.dataset.videoFramesPortrait || 0) || this.frameCount;
      this.videoFrames = this.videoFramesLandscape;
      // The frame URLs point at real assets (frame 0000) so Theme Check can
      // resolve them; the index is substituted here rather than in Liquid.
      // There are two sequences because there is no honest way to crop one:
      // the phone set is rendered in its own aspect from its own camera.
      this.frameUrl = root.dataset.frameUrl;
      this.frameUrlPortrait = root.dataset.frameUrlPortrait || null;
      this.videoUrls = root.dataset.videoPortrait ? JSON.parse(root.dataset.videoPortrait) : null;
      this.videoLandscape = root.dataset.videoLandscape ? JSON.parse(root.dataset.videoLandscape) : null;
      this.mode = 'frames';        // 'frames' (sharp canvas, desktop) or 'video' (phones: the video is the only source)
      this.hybrid = false;         // desktop: a video is in and carries the motion
      this.motionReady = false;    // any video tier is in
      this.motionLoading = null;   // the full tier's fetch
      this.tiers = [];             // [{ name, rank, blobUrl, slots, ready, abort }]
      this.video = null;           // the best tier's first element (the proofs read its size)
      this.front = null;           // the slot whose pose is on the motion canvas
      this.blend = 1;              // 0..1, the front buffer over the back one
      this.blendAt = 0;
      this.blendMs = 60;
      this.lastDir = 1;
      this.fps = 30;
      this.source = '';            // what is on screen: 'sharp', 'nearest' or 'video'
      this.wantVideo = false;
      this.inMotion = false;       // hysteresis, so the source does not flicker at the threshold
      this.stillSince = 0;         // when the reader last came to rest (a dwell gates the sharp fetch)
      // slower than this (progress per second) the reader is looking, not moving
      this.stillBelow = Number(root.dataset.stillBelow || 0.12);
      this.portraitBelow = Number(root.dataset.portraitBelow || 1.1);
      this.usingPortrait = null;
      this.loading = null;
      this.sharpShown = false;
      this.releasePreviewWhenReplaced = false;
      this.onScreen = false;     // the section intersects the viewport
      this.primed = false;

      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // How many decoded sharp frames may be resident at once: a 2560-wide
      // frame is 15 MB decoded, so this is a memory budget. Only the poses the
      // reader rests on are ever fetched once a video is in.
      this.small = small;
      this.maxResident = Number(root.dataset.maxResident || (small ? 12 : 14));
      this.baseResident = this.maxResident;
      this.lookAhead = 8;
      this.active = false;
      this.released = false;
      this.lastWidth = window.innerWidth;
      // measurements a proof can read: how often a pose other than the wanted
      // one was shown, every pose change, and how far behind the picture ran
      this.substitutes = 0;
      this.paints = 0;
      this.paintTimes = [];
      this.lagMax = 0;         // poses between what was wanted and what was shown
      this.stallMax = 0;       // ms the shown pose stayed behind the wanted one
      this.stallSince = 0;
      this.seekTimes = [];     // how long each seek took to land, in ms
      this.presentedBy = { callback: 0, timer: 0, seeked: 0, codec: 0 };   // which signal said a frame was up
      // WebCodecs is the decoder wherever it exists; ?ltdecoder=video forces
      // the element path, so the two can be measured against each other
      this.codecAllowed = !root.dataset.noCodec && !/[?&]ltdecoder=video\b/.test(window.location.search);
      this.codecFailed = false;
      this.events = [];   // what this chapter did and when, so a proof can name a hitch

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.tick = this.tick.bind(this);
    }

    note(what) {
      this.events.push({ t: Math.round(performance.now()), what });
      if (this.events.length > 60) this.events.shift();
    }

    // Which sequence suits the stage as it is right now.
    wantsPortrait() {
      if (!this.frameUrlPortrait) return false;
      const r = this.canvas.getBoundingClientRect();
      if (!r.height) return false;
      return r.width / r.height < this.portraitBelow;
    }

    // The site header is fixed, ivory, and overlays whatever is beneath it.
    // While a light scene is on screen the header is told to go dark.
    watchHeaderTone() {
      if (!this.root.classList.contains('lautio-rack--ontint')) return;
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          this.note(e.isIntersecting ? 'header-light' : 'header-dark');
          document.body.classList.toggle('lautio-on-light', e.isIntersecting);
        }
      }, { threshold: 0, rootMargin: '-70px 0px -85% 0px' });
      io.observe(this.root);
    }

    // The size selector in the story and the one in the commerce block are the
    // same choice. The commerce element owns the state (it also writes it to the
    // URL); this just asks it to change and mirrors whatever it settles on.
    wireSizes() {
      const buttons = [...this.root.querySelectorAll('[data-lautio-size]')];
      if (!buttons.length) return;
      const commerce = () => document.querySelector('lautio-capture-locked');
      const reflect = (size) => buttons.forEach((b) =>
        b.setAttribute('aria-checked', String(b.dataset.lautioSize === size)));
      buttons.forEach((b) => b.addEventListener('click', () => {
        const size = b.dataset.lautioSize;
        const el = commerce();
        if (el && typeof el.selectSize === 'function') el.selectSize(size);
        else reflect(size);
      }));
      const el = commerce();
      if (!el) return;
      if (el.dataset.selectedSize) reflect(el.dataset.selectedSize);
      new MutationObserver(() => reflect(el.dataset.selectedSize))
        .observe(el, { attributes: true, attributeFilter: ['data-selected-size'] });
    }

    async init() {
      this.watchHeaderTone();
      this.wireSizes();
      this.resizeCanvas();
      window.addEventListener('resize', this.onResize, { passive: true });

      // Nothing is decoded until the chapter is within a viewport and a half of
      // the reader, and everything is released again once it is well past. The
      // poster stays in the markup, so there is never an empty stage.
      if ('IntersectionObserver' in window) {
        // a phone keeps decoders only for the chapter on screen and the next:
        // iOS loads a handful of video elements at once, no more
        const nearMargin = this.small ? '100% 0px 100% 0px' : '150% 0px 150% 0px';
        const farMargin = this.small ? '160% 0px 160% 0px' : '250% 0px 250% 0px';
        const near = new IntersectionObserver((entries) => {
          for (const e of entries) if (e.isIntersecting) this.activate();
        }, { threshold: 0, rootMargin: nearMargin });
        const far = new IntersectionObserver((entries) => {
          for (const e of entries) if (!e.isIntersecting) this.release();
        }, { threshold: 0, rootMargin: farMargin });
        near.observe(this.root);
        far.observe(this.root);
        // the poster gives way only once the chapter is on screen with a
        // picture drawn, so a reader never scrolls into a bare stage
        const screen = new IntersectionObserver((entries) => {
          for (const e of entries) {
            this.onScreen = e.isIntersecting;
            if (this.onScreen && this.ready) { this.shown(); this.draw(true); }
          }
        }, { threshold: 0, rootMargin: '0px' });
        screen.observe(this.root);
      } else {
        this.onScreen = true;
        this.activate();
      }
      // The preview is a few hundred kilobytes: it is fetched for every chapter
      // at load - into the browser's cache only, no elements yet - so the
      // chapter's own fetch on activation is instant. The full video follows
      // when the chapter is near.
      if (!this.reduced) this.primePreview();
    }

    primePreview() {
      const urls = this.currentUrls();
      if (!urls || !urls.preview || this.primed) return;
      this.primed = true;
      try { fetch(urls.preview, { cache: 'force-cache', priority: 'low' }).then((r) => r && r.blob()).catch(() => {}); } catch (e) { /* no fetch */ }
    }

    async activate() {
      if (this.active) return;
      this.note('activate');
      this.active = true;
      if (!this.ready || this.released) {
        this.released = false;
        try {
          await this.preload();
        } catch (err) {
          if (!this.ready) this.fail('Could not load the sequence');
          this.active = false;
          return;
        }
        if (!this.active) return;   // released while loading
      }
      if (!this.ready) this.start();
      else this.resume();
    }

    release() {
      if (!this.active) return;
      const t0 = performance.now();
      this.active = false;
      if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
      if (!this.tiers.some((t) => t.codec)) this.generation += 1;
      this.closeFrames();
      this.drawnIndex = -1;
      // Every video element goes: a phone has a handful of hardware decoders,
      // and four chapters holding several each is how iOS runs out of them.
      // A decoder tier is parked instead - its frames closed now, the
      // decoder and file kept - and torn down at the next pause, unless the
      // chapter is back by then.
      const parked = this.tiers.filter((t) => t.codec);
      for (const t of parked) t.codec.park();
      this.front = null;
      this.hasBack = false;
      this.blend = 1;
      this.releasePreviewWhenReplaced = false;
      for (const t of this.tiers.slice()) if (!t.codec) this.dropTier(t.name);
      if (parked.length) {
        const gen = this.generation;
        whenStill(() => { if (!this.active && this.generation === gen) for (const t of parked) if (this.tiers.includes(t)) this.dropTier(t.name); });
      }
      this.released = true;
      mark('release', t0);
      this.note('release');
    }

    closeFrames() {
      this.frameGen += 1;   // decodes still in flight are dropped, not leaked
      for (let i = 0; i < this.frames.length; i += 1) {
        const b = this.frames[i];
        if (b && b.close) b.close();
        this.frames[i] = undefined;
      }
      this.resident.length = 0;
      // the canvases keep their pixels
    }

    resume() {
      this.clearFailure();
      this.onScroll();
      this.rendered = this.target;
      this.drawnIndex = -1;
      this.draw(true);
      this.lastT = performance.now();
      if (!this.reduced && !this.raf) this.raf = requestAnimationFrame(this.tick);
    }

    start() {
      this.ready = true;
      this.clearFailure();
      this.onScroll();
      this.rendered = this.target;          // no slide-in on first paint
      this.draw(true);

      if (this.reduced) {
        // static: follow scroll exactly, no spring, no animation loop
        window.addEventListener('scroll', () => {
          this.onScroll();
          this.rendered = this.target;
          this.draw(false);
        }, { passive: true });
        return;
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.lastT = performance.now();
      this.raf = requestAnimationFrame(this.tick);
    }

    // the poster gives way once a canvas holds a picture and the chapter is
    // on screen
    shown() {
      if (!this.onScreen) return;
      if (this.motionIndex < 0 && !this.sharpShown) return;
      if (!this.root.classList.contains('is-ready')) this.root.classList.add('is-ready');
    }

    clearFailure() {
      this.root.classList.remove('is-failed');
      if (this.status) { this.status.textContent = ''; this.status.hidden = true; }
    }

    fail(message) {
      this.root.classList.add('is-failed');
      if (this.status) this.status.textContent = message;
    }

    frameUrl_(i) {
      const base = this.usingPortrait ? this.frameUrlPortrait : this.frameUrl;
      return base.replace(/0000(?=\.webp)/, String(i).padStart(4, '0'));
    }

    // ------------------------------------------------------------ sharp frames
    // A sliding window, not a bulk preload: a bounded number of frames are held
    // decoded around where the reader is, fetched as they are approached and
    // evicted once they are far behind.
    async ensure(i) {
      if (i < 0 || i >= this.frameCount) return null;
      if (this.frames[i]) return this.frames[i];
      if (this.inflight.has(i)) return this.inflight.get(i);
      const gen = this.frameGen;
      const job = this.loadBitmap(this.frameUrl_(i))
        .then((bmp) => {
          if (gen !== this.frameGen) { if (bmp.close) bmp.close(); return null; }
          this.frames[i] = bmp;
          this.resident.push(i);
          this.evict();
          // reduced motion runs without an animation loop, so the frame that has
          // just arrived is the only thing that can put itself on screen
          if (this.ready && !this.raf && i === this.sharpIndex()) this.draw(true);
          return bmp;
        })
        .catch(() => null)
        .finally(() => this.inflight.delete(i));
      this.inflight.set(i, job);
      return job;
    }

    evict() {
      while (this.resident.length > this.maxResident) {
        // distance from where the reader IS, not from the frame on the canvas
        const here = this.sharpIndex();
        let worst = 0;
        for (let k = 1; k < this.resident.length; k += 1) {
          if (Math.abs(this.resident[k] - here) > Math.abs(this.resident[worst] - here)) worst = k;
        }
        const i = this.resident.splice(worst, 1)[0];
        // never drop the frame currently on screen
        if (i === this.drawnIndex) { this.resident.push(i); return; }
        const bmp = this.frames[i];
        if (bmp && bmp.close) bmp.close();
        this.frames[i] = undefined;
      }
    }

    nearestLoaded(i) {
      if (this.frames[i]) return i;
      for (let d = 1; d < this.frameCount; d += 1) {
        if (this.frames[i - d]) return i - d;
        if (this.frames[i + d]) return i + d;
      }
      return -1;
    }

    sharpIndex() {
      return clamp(Math.round(this.rendered * (this.frameCount - 1)), 0, this.frameCount - 1);
    }

    videoIndex() {
      return clamp(Math.round(this.rendered * (this.videoFrames - 1)), 0, this.videoFrames - 1);
    }

    // the video pose that shows exactly what sharp frame k shows
    poseOfSharp(k) {
      if (this.videoFrames === this.frameCount) return k;
      return Math.round(k * (this.videoFrames - 1) / (this.frameCount - 1));
    }

    // Enough to start with, before any video: a spread across the move so
    // wherever the reader enters there is something to draw.
    async preload() {
      const portrait = this.wantsPortrait();
      if (portrait !== this.usingPortrait) {
        // a different orientation is a different sequence and a different video
        this.generation += 1;
        this.closeFrames();
        this.dropVideo();
        this.usingPortrait = portrait;
      }
      this.videoFrames = portrait ? this.videoFramesPortrait : this.videoFramesLandscape;
      // a chapter coming back with its decoder parked has its motion already:
      // no fetch, no frames, straight back to the mode the tier put it in
      const kept = this.bestTier();
      if (kept && kept.ready && kept.codec) { this.onTierReady(kept); return; }
      this.mode = 'frames';
      const urls = portrait ? this.videoUrls : this.videoLandscape;
      if (urls && !this.reduced) {
        // the preview first, alone on the connection; the full video only
        // once it is in - on a phone the two raced and the preview that
        // should have moved the chapter within a second took four
        const pv = this.loadPreview();
        if (pv) pv.then(() => { if (this.active && !this.released) this.loadMotion(urls); });
        else this.loadMotion(urls);
      }
      const first = [];
      if (urls && urls.preview && portrait && !this.reduced) {
        // A phone with a preview on the way fetches no sharp frames at all:
        // two dozen 170 KB frames raced the preview for the connection and
        // the chapter stepped between far-apart poses while both arrived.
        const pv = this.loadPreview();
        if (pv) { try { await pv; } catch (e) { /* the frames below carry it */ } }
        if (this.motionReady) return;
      }
      if (urls && !portrait && !this.reduced) {
        // Desktop: a sharp frame is 800 KB and a video is on its way, so eight
        // across the move is the whole preload; every one is a frame the
        // window would want at rest anyway.
        const step = Math.max(1, Math.round(this.frameCount / 8));
        for (let i = 0; i < this.frameCount; i += step) first.push(i);
      } else {
        // Phones without a preview (and reduced motion) keep the close spread:
        // until the video lands the nearest loaded frame is what is drawn.
        for (let i = 0; i < this.frameCount; i += 5) first.push(i);
        for (let i = 0; i < Math.min(8, this.frameCount); i += 1) if (!first.includes(i)) first.push(i);
        first.sort((a, b) => a - b);
      }
      const queue = first.slice();
      const workers = new Array(8).fill(0).map(async () => {
        while (queue.length) await this.ensure(queue.shift());
      });
      await Promise.all(workers);
      // a video landing first closes the frames on purpose (phones), which is
      // success, not the failure an empty set would otherwise mean
      if (this.mode !== 'video' && !this.motionReady && !this.frames.some(Boolean)) throw new Error('no frames decoded');
    }

    // Only one orientation is ever fetched. If the stage changes shape past the
    // threshold the other one is loaded in the background and swapped in.
    ensureCorrectSequence() {
      if (!this.frameUrlPortrait || this.loading) return;
      if (this.wantsPortrait() === this.usingPortrait) return;
      // the poster covers the swap: every decoded frame is closed before the
      // new sequence has any
      this.root.classList.remove('is-ready');
      this.motionIndex = -1;
      this.hideSharp();
      this.loading = this.preload()
        .then(() => { this.drawnIndex = -1; this.wantedIndex = -1; this.draw(true); })
        .catch(() => { this.root.classList.add('is-ready'); })
        .finally(() => { this.loading = null; this.ensureCorrectSequence(); });
    }

    // Sharp frames are fetched only where the reader comes to rest.
    prefetch() {
      if (this.mode === 'video') return;
      const here = this.sharpIndex();
      const dir = this.velocity >= 0 ? 1 : -1;
      const fast = Math.abs(this.velocity) > 0.35;
      if (this.hybrid && this.motionReady) {
        // A stuttering scroll (a trackpad, a wheel) is a string of brief stops,
        // so a frame is only worth its 800 KB once the reader has settled.
        const still = !this.inMotion && (performance.now() - this.stillSince) > 220;
        if (!this.frames[here]) {
          if (still || this.inflight.size === 0) this.ensure(here);
          return;
        }
        if (!still) return;   // neighbours only once the reader has stopped
        let asked = 0;
        for (let d = 1; d <= 3 && asked < 4; d += 1) {
          for (const i of [here + d * dir, here - d * dir]) {
            if (i >= 0 && i < this.frameCount && !this.frames[i] && !this.inflight.has(i)) { this.ensure(i); asked += 1; }
          }
        }
        return;
      }
      // No video yet: a small window ahead of the reader. While one is on its
      // way the window stays small - pulling half a sequence that is about to
      // be replaced is what made a phone fetch 25 MB.
      const waiting = !this.motionReady && !!(this.motionLoading || (this.usingPortrait ? this.videoUrls : this.videoLandscape));
      const budget = waiting ? (fast ? 4 : 3) : (fast ? 10 : 6);
      const reach = waiting ? 5 : (fast ? this.lookAhead * 2 : this.lookAhead);
      let asked = 0;
      for (let d = 1; d <= reach && asked < budget; d += 1) {
        for (const i of [here + d * dir, here - d * dir]) {
          if (i >= 0 && i < this.frameCount && !this.frames[i] && !this.inflight.has(i)) {
            this.ensure(i);
            asked += 1;
            if (asked >= budget) break;
          }
        }
      }
    }

    // ------------------------------------------------------------- the videos
    pickVideoSource(urls) {
      const probe = document.createElement('video');
      const h264 = probe.canPlayType('video/mp4; codecs="avc1.640033"') || probe.canPlayType('video/mp4');
      if (h264 && urls.mp4) return urls.mp4;
      const vp9 = probe.canPlayType('video/webm; codecs="vp9"');
      if (vp9 && urls.webm) return urls.webm;
      return urls.mp4 || urls.webm;
    }

    tier(name) { return this.tiers.find((t) => t.name === name) || null; }

    bestTier() {
      let best = null;
      for (const t of this.tiers) if (t.ready && (!best || t.rank > best.rank)) best = t;
      return best;
    }

    currentUrls() {
      if (this.usingPortrait === null) {
        this.usingPortrait = this.wantsPortrait();
        this.videoFrames = this.usingPortrait ? this.videoFramesPortrait : this.videoFramesLandscape;
      }
      return this.usingPortrait ? this.videoUrls : this.videoLandscape;
    }

    // Decoders per tier. A phone has a handful of hardware decoders in all,
    // so it gets the fewest that still pipeline: two for the move, one for
    // the preview that only bridges the first seconds.
    loadPreview() {
      const urls = this.currentUrls();
      if (!urls || !urls.preview) return null;
      return this.loadTier('preview', 1, urls.preview, this.usingPortrait ? 1 : 2, 'high');
    }

    // The whole file is fetched first, so every seek is local: a video that
    // streams can only seek inside what it has buffered, and a scrub jumps
    // around the whole chapter.
    loadMotion(urls) {
      if (this.tier('full')) return this.motionLoading;
      const src = this.pickVideoSource(urls);
      if (!src) return null;
      const count = this.usingPortrait ? 2 : 3;
      // the preview is in by the time this runs, so the full file has the
      // connection; 'low' let a page's images push a 5 MB video behind them
      this.motionLoading = this.loadTier('full', 2, src, count, 'auto').finally(() => { this.motionLoading = null; });
      return this.motionLoading;
    }

    loadTier(name, rank, src, count, priority) {
      const existing = this.tier(name);
      if (existing) return existing.promise;
      const gen = this.generation;
      const abort = ('AbortController' in window) ? new AbortController() : null;
      const tier = { name, rank, blobUrl: null, slots: [], ready: false, abort, gen, promise: null, priority: priority || 'auto' };
      this.tiers.push(tier);
      tier.promise = this.fetchTier(tier, src, count);
      return tier.promise;
    }

    async fetchTier(tier, src, count) {
      try {
        await this.fetchTierBody(tier, src, count);
      } finally {
        // abandoned (released, turned, failed): out of the list, so the next
        // activate fetches again instead of waiting on a tier that never comes
        if (!tier.ready && this.tier(tier.name) === tier) {
          this.discardTier(tier);
          this.tiers = this.tiers.filter((t) => t !== tier);
        }
      }
    }

    async fetchTierBody(tier, src, count) {
      const { name, gen, abort } = tier;
      try {
        // the priority hint is honoured where the engine knows it and ignored elsewhere
        const res = await fetch(src, { cache: 'force-cache', signal: abort ? abort.signal : undefined, priority: tier.priority });
        if (!res.ok) throw new Error(`${res.status} ${name} video`);
        const tb = performance.now();
        const buffer = await this.readWhole(res);
        mark('tier-buffer', tb);
        if (gen !== this.generation || this.tier(name) !== tier) return;   // released or turned meanwhile
        // WebCodecs first: the file demuxed, one decoder, no element at all
        if (this.codecAllowed && !this.codecFailed && typeof VideoDecoder === 'function') {
          let demuxed = null;
          const td = performance.now();
          try { demuxed = demuxMp4(buffer); } catch (e) { demuxed = null; }
          mark('tier-demux', td);
          if (demuxed && demuxed.samples.length && await decoderSupported(demuxed)) {
            if (gen !== this.generation || this.tier(name) !== tier) return;
            try {
              tier.codec = new PoseDecoder(demuxed, {
                onFrame: (i) => this.onCodecFrame(tier, i),
                onError: () => this.codecBroke(),
              });
              // the stand-in for a slot: what the canvas shows is read from here
              tier.pseudo = { el: null, tier, index: -1, busy: false, want: -1, presented: true, ready: true, codec: true };
              tier.ready = true;
              this.onTierReady(tier);
              return;
            } catch (e) {
              tier.codec = null;
            }
          }
        }
        const blob = new Blob([buffer], { type: 'video/mp4' });
        const url = URL.createObjectURL(blob);
        tier.blobUrl = url;
        // The first decoder makes the tier ready; the others join as they
        // settle. iOS loads only a handful of video elements at once - one
        // that never settles must not hold the tier, and the preview it
        // replaces, hostage.
        const first = this.makeSlot(tier, url);
        tier.slots.push(first);
        await this.settle(first.el);
        if (gen !== this.generation || this.tier(name) !== tier) { this.discardTier(tier); return; }
        first.ready = true;
        tier.ready = true;
        this.onTierReady(tier);
        for (let k = 1; k < count; k += 1) {
          const extra = this.makeSlot(tier, url);
          tier.slots.push(extra);
          this.settle(extra.el, 4000).then(() => { extra.ready = true; }).catch(() => {
            // it did not come: out, so it holds no decoder
            if (this.tier(name) === tier) { tier.slots = tier.slots.filter((s) => s !== extra); try { extra.el.removeAttribute('src'); extra.el.load(); } catch (e) { /* nothing */ } extra.el.remove(); }
          });
        }
      } catch (err) {
        // the chapter goes on without this tier; fetchTier's finally drops it
      }
    }

    // The body in pieces, into one buffer: arrayBuffer() on a 10 MB response
    // is one copy on the main thread, and WebKit made it a visible hitch.
    async readWhole(res) {
      const total = Number(res.headers.get('content-length') || 0);
      if (!res.body || !res.body.getReader || !total) return res.arrayBuffer();
      const out = new Uint8Array(total);
      const reader = res.body.getReader();
      let at = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (at + value.length > total) return res.arrayBuffer ? new Blob([out.subarray(0, at), value]).arrayBuffer() : out.buffer;
        out.set(value, at);
        at += value.length;
      }
      return at === total ? out.buffer : out.buffer.slice(0, at);
    }

    // A decoder: a hidden element that is never shown, only drawn from.
    makeSlot(tier, url) {
      const el = document.createElement('video');
      el.muted = true;
      el.playsInline = true;
      el.setAttribute('playsinline', '');
      el.setAttribute('muted', '');
      el.preload = 'auto';
      el.setAttribute('aria-hidden', 'true');
      el.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;';
      const slot = { el, tier, index: -1, busy: false, want: -1, presented: false, seekedAt: 0, seekAt: 0, seekGen: 0, presentedEarly: false, ready: false, proven: 0 };
      el.addEventListener('seeked', () => this.onSeeked(slot));
      this.stage.appendChild(el);
      el.src = url;
      return slot;
    }

    settle(v, timeoutMs) {
      return new Promise((ok, rej) => {
        let timer = 0;
        const done = () => { if (v.readyState >= 2) { cleanup(); ok(); } };
        const fail = () => { cleanup(); rej(new Error('video failed')); };
        const cleanup = () => { v.removeEventListener('loadeddata', done); v.removeEventListener('canplay', done); v.removeEventListener('error', fail); if (timer) clearTimeout(timer); };
        v.addEventListener('loadeddata', done);
        v.addEventListener('canplay', done);
        v.addEventListener('error', fail);
        v.load();
        setTimeout(() => { if (v.readyState >= 2) done(); }, 50);
        if (timeoutMs) timer = setTimeout(() => { if (v.readyState >= 2) done(); else fail(); }, timeoutMs);
      });
    }

    // what the proofs read as "the video": the element, or the decoder's file
    describeTier(tier) {
      if (!tier) return null;
      if (tier.codec) return { decoder: 'webcodecs', videoWidth: tier.codec.d.width, videoHeight: tier.codec.d.height, duration: tier.codec.n / this.fps };
      return tier.slots[0] ? tier.slots[0].el : null;
    }

    onTierReady(tier) {
      this.note(`tier-ready:${tier.name}`);
      this.motionReady = true;
      this.video = this.describeTier(tier);
      // the preview's decoder goes the moment the full one has drawn: on a
      // phone every video element counts
      if (tier.name === 'full' && this.tier('preview')) this.releasePreviewWhenReplaced = true;
      if (this.usingPortrait) {
        // phones: the video is the only source from here on (closeFrames
        // drops the decodes in flight; the tier generation is left alone, or
        // the full video's fetch, started with it, would be thrown away)
        this.mode = 'video';
        this.closeFrames();
      } else {
        this.hybrid = true;
        // sharp frames now only serve the rest states: a smaller window
        this.maxResident = Math.min(this.maxResident, 8);
        this.evict();
      }
      // the better tier takes over at the pose the reader is on
      if (this.ready) this.draw(true);
    }

    discardTier(tier) {
      if (tier.codec) { tier.codec.destroy(); tier.codec = null; }
      if (this.front && this.front.tier === tier) this.front = null;
      for (const s of tier.slots) {
        try { s.el.removeAttribute('src'); s.el.load(); } catch (e) { /* nothing to free */ }
        s.el.remove();
        if (this.front === s) this.front = null;
      }
      tier.slots = [];
      if (tier.blobUrl) { try { URL.revokeObjectURL(tier.blobUrl); } catch (e) { /* already gone */ } tier.blobUrl = null; }
      tier.ready = false;
    }

    dropTier(name) {
      const t = this.tier(name);
      if (!t) return;
      this.note(`drop:${name}`);
      if (t.abort) { try { t.abort.abort(); } catch (e) { /* already done */ } }
      this.discardTier(t);
      this.tiers = this.tiers.filter((x) => x !== t);
      const best = this.bestTier();
      this.video = this.describeTier(best);
      if (!best) {
        this.motionReady = false;
        this.hybrid = false;
        this.maxResident = this.baseResident;
        if (this.mode === 'video') this.mode = 'frames';
      }
    }

    dropVideo() {
      for (const t of this.tiers.slice()) this.dropTier(t.name);
      this.front = null;
      this.hasBack = false;
      this.blend = 1;
      this.releasePreviewWhenReplaced = false;
    }

    // The time in the file that holds exactly this pose: the middle of the
    // frame, because a seek lands on the frame the time falls inside.
    timeOf(slot, index) {
      const d = slot.el.duration;
      if (!d) return 0;
      return Math.min((index + 0.5) / this.fps, Math.max(0, d - 1 / (this.fps * 2)));
    }

    indexOf(slot) {
      return clamp(Math.floor(slot.el.currentTime * this.fps + 1e-4), 0, this.videoFrames - 1);
    }

    // 'seeked' says the frame is decoded, not that it can be drawn: a slot
    // counts as ready only once the frame has been presented
    // (requestVideoFrameCallback), with a short timer as the fallback for
    // engines without it.
    seekSlot(slot, index) {
      if (slot.presented && slot.index === index) return;   // already there: a seek would fire 'seeked' at once and spin
      slot.busy = true;
      slot.want = index;
      slot.seekAt = performance.now();
      slot.seekedAt = 0;
      slot.presented = false;
      slot.presentedEarly = false;
      slot.seekGen += 1;
      const gen = slot.seekGen;
      try {
        slot.el.currentTime = this.timeOf(slot, index);
      } catch (e) {
        slot.busy = false;
        return;
      }
      if (typeof slot.el.requestVideoFrameCallback === 'function') {
        slot.el.requestVideoFrameCallback(() => {
          if (slot.seekGen !== gen) return;
          if (slot.seekedAt) { this.presentedBy.callback += 1; this.onPresented(slot); } else slot.presentedEarly = true;
        });
      }
    }

    onSeeked(slot) {
      slot.index = this.indexOf(slot);
      slot.seekedAt = performance.now();
      if (slot.seekAt) { this.seekTimes.push(slot.seekedAt - slot.seekAt); if (this.seekTimes.length > 240) this.seekTimes.shift(); }
      // The frame is drawable at 'seeked' in every engine measured; the
      // presentation callback is not waited for, because Chrome fires it
      // for a hidden element only about fifteen times a second, and that
      // capped the whole pipeline. What guards against an empty frame is
      // copyFrame itself, which checks the pixels it copied.
      slot.presentedEarly = false;
      this.presentedBy.seeked += 1;
      this.onPresented(slot);
    }

    onPresented(slot) {
      if (slot.presented) return;
      slot.presented = true;
      slot.busy = false;
      if (!this.ready || !this.active || !slot.tier.ready) return;
      if (this.mode !== 'video' && !this.wantVideo) return;
      const want = this.videoIndex();
      const off = Math.abs(slot.index - want);
      const frontOff = this.front ? Math.abs(this.front.index - want) : Infinity;
      if (off === 0) {
        const better = !this.front || this.front.index !== want || slot.tier.rank > this.front.tier.rank || (slot === this.front && this.motionIndex !== want);
        if (better) this.promote(slot);
        else this.prefetchVideo(want);
        return;
      }
      // It landed near the wanted pose but not on it. With a dense video a
      // fast reader outruns the seeks, and waiting for an exact hit froze the
      // picture: whatever is closer than what is shown goes up, and the next
      // seek aims ahead again.
      const dir = this.velocity > 0.02 ? 1 : (this.velocity < -0.02 ? -1 : 0);
      const ahead = this.front ? (slot.index - this.front.index) * dir > 0 : true;
      if ((off < frontOff || (off === frontOff && ahead)) && (slot !== this.front || this.motionIndex !== slot.index) && slot.tier.rank >= (this.front ? this.front.tier.rank : 0)) this.promote(slot);
      this.scheduleVideo(want);
    }

    // how many poses the reader covers while a seek is in flight, so a seek
    // aims where they will be rather than where they were
    lead() {
      const perSecond = Math.abs(this.velocity) * (this.videoFrames - 1);
      return Math.round(perSecond * Math.max(35, this.seekLatency() + 12) / 1000);
    }

    // the median of the last seeks, so the aim and the pipeline depth follow
    // what this engine actually does with this file
    seekLatency() {
      const n = this.seekTimes.length;
      if (n < 4) return 20;
      const a = this.seekTimes.slice(-24).sort((x, y) => x - y);
      return a[Math.floor(a.length / 2)];
    }

    // a decoder that may be seeked: any ready one but the pose on the canvas
    // (its frame is already in a buffer, so a blend never needs the decoder)
    free(slot) {
      return slot.ready && !slot.busy && slot !== this.front;
    }

    // The decoder's frame goes into the spare buffer, which becomes the
    // front. A frame that copies as black (a decoder mid-flush) is refused:
    // the buffers are swapped back and the caller waits for the next signal.
    copyFrame(slot) {
      // A decoder caught mid-flush hands over a black frame, so each one used
      // to be checked by reading a 16 x 16 thumbnail back with getImageData.
      // That readback is a synchronous GPU stall: measured at a median of
      // 4.5 ms and up to 15 ms per pose change, 274 ms of the 282 ms this
      // runtime spent in a two-second scroll. It was not a guard against the
      // stutter, it WAS the stutter.
      //
      // A decoder only ever handed over black just after it was created, so
      // each one is checked for its first few frames and trusted afterwards.
      // In the steady state a pose change now costs a single drawImage.
      if (slot.proven < 3) {
        const pc = this.probeCtx;
        pc.drawImage(slot.el, 0, 0, 16, 16);
        const d = pc.getImageData(0, 0, 16, 16).data;
        let max = 0;
        for (let j = 0; j < d.length; j += 4) { const l = d[j] + d[j + 1] + d[j + 2]; if (l > max) max = l; }
        if (max < 24) return false;   // nothing but black: not ready after all
        slot.proven += 1;
      }
      const b = this.bufBack;
      this.cover(b.getContext('2d', { alpha: false }), slot.el, b.width, b.height);
      this.bufBack = this.bufFront;
      this.bufFront = b;
      return true;
    }

    // The slot that holds the wanted pose is drawn into the motion canvas. At a
    // reading pace the poses are neighbours and the new one blends in over
    // the old for a few refreshes - the picture is always between two poses
    // rather than stepping - while a jump of several poses (a flick) cuts.
    promote(slot) {
      const prev = this.front;
      // the new pose is copied out of its decoder once; the previous front's
      // copy is the base of the blend
      if (!this.copyFrame(slot)) {
        // an empty frame: the decoder is not ready after all - it is drawn
        // when the presentation callback or the timer says so
        slot.presented = false;
        slot.busy = true;
        this.presentedBy.refused = (this.presentedBy.refused || 0) + 1;
        return;
      }
      // the same decoder re-seeked (a single-decoder tier) never blends
      this.presentFront(slot, prev ? prev.index : -1, !!prev && prev !== slot && prev.tier === slot.tier);
    }

    // ---------------------------------------------------- the decoder path
    // The pose the reader is on goes up if it is decoded; failing that, a
    // decoded pose between the one shown and the one wanted is progress. The
    // decoder is then told where the reader is, so it decodes ahead.
    codecShow(tier, index) {
      const dec = tier.codec;
      const dir = this.velocity > 0.02 ? 1 : (this.velocity < -0.02 ? -1 : this.lastDir);
      this.lastDir = dir;
      const shown = this.front ? this.front.index : -1;
      const sameTier = !!this.front && this.front.tier === tier;
      if (!sameTier || shown !== index || this.motionIndex !== index) {
        if (dec.get(index)) this.promoteCodec(tier, index);
        else {
          const near = dec.nearest(index, sameTier ? shown : -1, dir);
          if (near >= 0 && (!this.front || Math.abs(near - index) < Math.abs(shown - index))) this.promoteCodec(tier, near);
        }
      }
      dec.want(index, dir);
    }

    // a frame has come out of the decoder
    onCodecFrame(tier, i) {
      if (!this.ready || !this.active || !tier.ready || this.bestTier() !== tier) return;
      if (this.mode !== 'video' && !this.wantVideo) return;
      const want = this.videoIndex();
      const shown = this.front ? this.front.index : -1;
      const sameTier = !!this.front && this.front.tier === tier;
      if (i === want) {
        if (!sameTier || shown !== want || this.motionIndex !== want) this.promoteCodec(tier, i);
        return;
      }
      const dir = this.velocity > 0.02 ? 1 : (this.velocity < -0.02 ? -1 : this.lastDir);
      const frontOff = this.front ? Math.abs(shown - want) : Infinity;
      if (Math.abs(i - want) < frontOff && (!sameTier || (i - shown) * dir > 0)) this.promoteCodec(tier, i);
    }

    promoteCodec(tier, index) {
      const frame = tier.codec.get(index);
      if (!frame) return;
      const slot = tier.pseudo;
      const prev = this.front;
      const b = this.bufBack;
      this.cover(b.getContext('2d', { alpha: false }), frame, b.width, b.height);
      this.bufBack = this.bufFront;
      this.bufFront = b;
      slot.index = index;
      this.presentedBy.codec += 1;
      this.presentFront(slot, prev ? prev.index : -1, !!prev && prev.tier === tier);
    }

    // The decoder failed: every decoder tier goes, and the chapter is loaded
    // again through the element path, which does not come back here.
    codecBroke() {
      if (this.codecFailed) return;
      this.codecFailed = true;
      for (const t of this.tiers.slice()) if (t.codec) this.dropTier(t.name);
      const urls = this.currentUrls();
      if (!urls || !this.active) return;
      const pv = this.loadPreview();
      if (pv) pv.then(() => { if (this.active && !this.released) this.loadMotion(urls); });
      else this.loadMotion(urls);
    }

    // The pose in the front buffer goes on the canvas. At a reading pace the
    // poses are neighbours and the new one blends in over the old for a few
    // refreshes - the picture is always between two poses rather than
    // stepping - while a jump of several poses (a flick) cuts.
    presentFront(slot, prevIndex, sameTier) {
      const now = performance.now();
      const near = sameTier && prevIndex >= 0 && Math.abs(prevIndex - slot.index) <= 2;
      const rate = Math.abs(this.velocity) * (this.videoFrames - 1);   // poses per second
      const interval = rate > 0.5 ? 1000 / rate : 140;
      const ms = near ? clamp(Math.round(interval * 0.9), 32, 140) : 0;
      if (near && ms > 0) {
        this.hasBack = true;
        this.blend = 0;
        this.blendAt = now;
        this.blendMs = ms;
      } else {
        this.hasBack = false;
        this.blend = 1;
      }
      this.front = slot;
      this.source = 'video';
      this.paints += 1;
      this.paintTimes.push(now);
      if (this.paintTimes.length > 240) this.paintTimes.shift();
      // a stand-in is a pose more than one SHARP frame away from the wanted
      // one (three poses in a dense video): nearer than that the picture is
      // within the render's own step of the truth
      const tolerance = Math.max(1, Math.round(this.videoFrames / this.frameCount));
      if (Math.abs(slot.index - this.videoIndex()) > tolerance) this.substitutes += 1;
      this.paintMotion();
      // the full video has taken over from the preview at the same pose
      if (this.releasePreviewWhenReplaced && slot.tier.name === 'full') {
        this.releasePreviewWhenReplaced = false;
        const pv = this.tier('preview');
        if (pv && pv.codec) { pv.codec.park(); whenStill(() => { if (this.tier('preview') === pv) this.dropTier('preview'); }); }
        else setTimeout(() => { if (this.front && this.front.tier.name === 'full') this.dropTier('preview'); }, 80);
      }
    }

    // Draw the front buffer: over the base while a blend runs. The canvas
    // keeps the previous picture between draws, so it is never blank.
    paintMotion() {
      const f = this.front;
      if (!f) return;
      const { mctx, motion } = this;
      if (this.hasBack && this.blend < 1) {
        mctx.drawImage(this.bufBack, 0, 0, motion.width, motion.height);
        mctx.globalAlpha = this.blend;
        mctx.drawImage(this.bufFront, 0, 0, motion.width, motion.height);
        mctx.globalAlpha = 1;
      } else {
        mctx.drawImage(this.bufFront, 0, 0, motion.width, motion.height);
      }
      this.motionIndex = f.index;
      // the sharp canvas shows a different pose: it steps out, the motion shows
      if (this.sharpShown && this.poseOfSharp(this.drawnIndex) !== f.index) this.hideSharp();
      this.shown();
      if (this.progress) this.progress.style.setProperty('--progress', this.rendered.toFixed(3));
      if (this.rendered > 0.015 && !this.root.classList.contains('is-moving')) { this.note('is-moving'); this.root.classList.add('is-moving'); }
    }

    // Called every tick: carries a blend forward, and takes a slot whose
    // presentation callback never came.
    tidySlots(now) {
      if (this.hasBack && this.blend < 1) {
        this.blend = Math.min(1, (now - this.blendAt) / this.blendMs);
        this.paintMotion();
        if (this.blend >= 1) this.hasBack = false;
      }
      for (const t of this.tiers) for (const s of t.slots) {
        if (s.busy && s.seekedAt && !s.presented && now - s.seekedAt > 48) { this.presentedBy.timer += 1; this.onPresented(s); }
      }
    }

    scheduleVideo(index) {
      const best = this.bestTier();
      if (!best) return;
      this.wantedIndex = index;
      this.wantVideo = true;
      if (best.codec) { this.codecShow(best, index); return; }
      if (this.front && this.front.index === index && !this.front.busy) {
        // the pose is up; if it is the preview's, the full one is fetched for it
        if (this.front.tier !== best && !best.slots.some((s) => s.busy && s.want === index)) {
          const up = best.slots.find((s) => this.free(s) && s.index !== index);
          if (up) this.seekSlot(up, index);
        }
        this.prefetchVideo(index);
        return;
      }
      // a slot already sitting on the pose, or nearer it than the front: the
      // best tier's if there are two
      const frontOff = this.front ? Math.abs(this.front.index - index) : Infinity;
      const dir = this.velocity > 0.02 ? 1 : (this.velocity < -0.02 ? -1 : 0);
      let have = null;
      for (const t of this.tiers) if (t.ready) for (const s of t.slots) {
        if (s.busy || !s.presented || s === this.front) continue;
        const off = Math.abs(s.index - index);
        // nearer than the front, or as near but on the side the reader is heading
        const ahead = this.front ? (s.index - this.front.index) * dir > 0 : true;
        if ((off < frontOff || (off === frontOff && ahead)) && (!have || off < Math.abs(have.index - index) || s.tier.rank > have.tier.rank)) have = s;
      }
      if (have) { this.promote(have); if (have.index === index) { this.prefetchVideo(index); return; } }
      // on its way already?
      const aim = clamp(index + dir * this.lead(), 0, this.videoFrames - 1);
      if (best.slots.some((s) => (s.busy && Math.abs(s.want - aim) <= 1) || (!s.busy && s.presented && Math.abs(s.index - aim) <= 1))) return;
      // a free slot of the best tier; the one furthest from the pose first,
      // so a slot pre-seeked to the next pose is kept
      let idle = null;
      for (const s of best.slots) {
        if (!this.free(s)) continue;
        if (!idle || Math.abs(s.index - index) > Math.abs(idle.index - index)) idle = s;
      }
      if (!idle) {
        // a tier with a single decoder (the phone's preview) seeks the one it
        // has, front or not: the canvas keeps the picture while it does
        const readySlots = best.slots.filter((s) => s.ready);
        if (readySlots.length === 1 && !readySlots[0].busy) idle = readySlots[0];
        if (!idle) return;   // every decoder is seeking: the first to land re-aims
      }
      this.seekSlot(idle, aim);
      // and the decoder after that goes one step further, so two seeks are
      // in flight for a reader who is moving - unless this engine's seeks are
      // slow, where a second one in flight only queues behind the first
      if (dir !== 0 && this.seekLatency() < 25) this.prefetchVideo(aim);
    }

    // Free decoders go to the poses the reader is heading for, so they are
    // decoded before they are wanted.
    prefetchVideo(index) {
      const best = this.bestTier();
      if (!best) return;
      const dir = this.velocity > 0.02 ? 1 : (this.velocity < -0.02 ? -1 : this.lastDir);
      this.lastDir = dir;
      // the poses right after this one, in the direction of travel
      const wanted = [];
      for (let d = 1; d <= 2; d += 1) {
        const n = index + d * dir;
        if (n >= 0 && n < this.videoFrames) wanted.push(n);
      }
      for (const n of wanted) {
        if (best.slots.some((s) => (!s.busy && s.presented && s.index === n) || (s.busy && s.want === n))) continue;
        let idle = null;
        for (const s of best.slots) {
          if (!this.free(s)) continue;
          if (s.index === index) continue;   // keep the pose on screen decoded
          if (!idle || Math.abs(s.index - index) > Math.abs(idle.index - index)) idle = s;
        }
        if (!idle) return;
        this.seekSlot(idle, n);
      }
    }

    // ------------------------------------------------------- the sharp canvas
    showSharp(index) {
      const { ctx, canvas } = this;
      if (this.drawnIndex !== index) {
        this.cover(ctx, this.frames[index], canvas.width, canvas.height);
        this.drawnIndex = index;
      }
      if (!this.sharpShown) {
        this.root.classList.add('is-sharp');
        this.sharpShown = true;
      }
      this.source = 'sharp';
      this.shown();
    }

    hideSharp() {
      if (!this.sharpShown) return;
      this.root.classList.remove('is-sharp');
      this.sharpShown = false;
    }

    async loadBitmap(url) {
      const res = await fetch(url, { cache: 'force-cache' });
      if (!res.ok) throw new Error(`${res.status} ${url}`);
      return createImageBitmap(await res.blob());
    }

    resizeCanvas() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = this.canvas.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width * dpr));
      const h = Math.max(1, Math.round(rect.height * dpr));
      if (this.canvas.width !== w || this.canvas.height !== h) {
        this.canvas.width = w;
        this.canvas.height = h;
        // the bitmap is erased by the resize, so the sharp frame must repaint
        this.drawnIndex = -1;
        this.hideSharp();
      }
      // The motion canvas is drawn into many times a second, so it is sized
      // for the video it shows rather than the display: a 1920-wide frame
      // gains nothing from a 2880-wide canvas, and the draw costs half. A
      // phone is the exception: its frame is 1242 wide for a 1206-pixel
      // screen, and a 2x canvas between the two blurred every frame.
      const mdpr = Math.min(window.devicePixelRatio || 1, this.small ? 3 : 1.5);
      const mw = Math.max(1, Math.round(rect.width * mdpr));
      const mh = Math.max(1, Math.round(rect.height * mdpr));
      if (this.motion.width !== mw || this.motion.height !== mh) {
        this.motion.width = mw;
        this.motion.height = mh;
        for (const b of [this.bufA, this.bufB]) { b.width = mw; b.height = mh; }
        this.motionIndex = -1;
        this.hasBack = false;
        this.blend = 1;
        const f = this.front;
        if (f && f.codec) {
          const frame = f.tier.codec && f.tier.codec.get(f.index);
          if (frame) {
            const b = this.bufBack;
            this.cover(b.getContext('2d', { alpha: false }), frame, b.width, b.height);
            this.bufBack = this.bufFront;
            this.bufFront = b;
            this.paintMotion();
          }
        } else if (f && f.el && f.el.videoWidth) { this.copyFrame(f); this.paintMotion(); }
      }
    }

    onResize() {
      // A phone's address bar shows and hides while the reader scrolls, and
      // every one of those is a resize. The scroll is written only when the
      // width changes (a rotation, a window resized on a desktop), only by the
      // chapter the reader is inside, and never by an off-screen one.
      const held = this.target;
      const width = window.innerWidth;
      const widthChanged = this.lastWidth !== undefined && width !== this.lastWidth;
      this.lastWidth = width;
      this.resizeCanvas();
      this.ensureCorrectSequence();
      if (widthChanged && this.active && held > 0 && held < 1) {
        const rect = this.track.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        const travel = rect.height - (this.stage.getBoundingClientRect().height || window.innerHeight);
        if (travel > 0 && Number.isFinite(held)) window.scrollTo(0, Math.round(top + held * travel));
      }
      this.onScroll();
      // a phone's toolbar coming and going is a resize too: the spring is
      // only reset when the width changed (a rotation, a resized window)
      if (widthChanged) { this.rendered = this.target; this.velocity = 0; }
      this.draw(widthChanged);
    }

    onScroll() {
      const rect = this.track.getBoundingClientRect();
      // The move's length is the track less the STAGE, not less the window:
      // on an iPhone the window grows and shrinks by the toolbar's height as
      // the reader scrolls, and a progress measured against it jumped a few
      // poses back and forth every time the toolbar collapsed or came back.
      // The stage is a constant 100vh, so the mapping holds still.
      const stageH = this.stage.getBoundingClientRect().height || window.innerHeight;
      const travel = rect.height - stageH;
      if (travel <= 0) {
        this.target = 0;
        return;
      }
      this.target = clamp(-rect.top / travel, 0, 1);
      // Once the stage has stopped sticking and rides up with the page, its
      // copy would slide straight under the fixed header. Fade it out over the
      // first part of that exit instead; the stylesheet reads --leave.
      const leave = clamp((window.innerHeight - rect.bottom) / (0.38 * window.innerHeight), 0, 1);
      if (leave !== this.leave) {
        this.leave = leave;
        this.root.style.setProperty('--leave', leave.toFixed(3));
      }
    }

    tick(now) {
      const dt = clamp((now - this.lastT) / 1000, 0, 0.05);
      this.lastT = now;

      // At rest the picture settles on a pose that has a sharp frame, so the
      // sharp frame that fades in is exactly what the video was showing. With
      // one video pose per sharp frame this is the target itself.
      let goal = this.target;
      if (this.videoFrames !== this.frameCount && !this.inMotion) {
        goal = Math.round(this.target * (this.frameCount - 1)) / (this.frameCount - 1);
      }
      const dx = goal - this.rendered;
      // critically damped: no overshoot, and the same curve in both directions
      this.velocity += (this.omega * this.omega * dx - 2 * this.omega * this.velocity) * dt;
      this.velocity = clamp(this.velocity, -this.maxVelocity, this.maxVelocity);
      this.rendered = clamp(this.rendered + this.velocity * dt, 0, 1);

      if (Math.abs(dx) < 0.0002 && Math.abs(this.velocity) < 0.0015) {
        this.rendered = goal;
        this.velocity = 0;
      }

      this.draw(false);
      this.tidySlots(now);
      this.measure(now);
      this.prefetch();
      this.raf = requestAnimationFrame(this.tick);
    }

    // how far the picture runs behind the scroll, for the proofs
    measure(now) {
      if (this.source !== 'video' || !this.front) { this.stallSince = 0; return; }
      const lag = Math.abs(this.front.index - this.videoIndex());
      if (lag > this.lagMax) this.lagMax = lag;
      if (lag === 0) { this.stallSince = 0; return; }
      if (!this.stallSince) this.stallSince = now;
      else if (now - this.stallSince > this.stallMax) this.stallMax = now - this.stallSince;
    }

    draw(force) {
      if (!this.ready) return;
      const index = this.sharpIndex();
      const pose = this.videoIndex();
      if (this.mode === 'video') {
        if (!this.bestTier()) return;
        this.scheduleVideo(pose);
        return;
      }
      // Movement takes stillBelow to begin and half of that to end, so a
      // reader hovering around the threshold does not alternate between the
      // two sources on every tick.
      const speed = Math.abs(this.velocity);
      const wasMoving = this.inMotion;
      this.inMotion = speed > this.stillBelow ? true : (speed < this.stillBelow * 0.5 ? false : !!this.inMotion);
      if (this.inMotion) this.stillSince = Infinity;
      else if (wasMoving || !Number.isFinite(this.stillSince)) this.stillSince = performance.now();
      const moving = this.inMotion;
      const motion = this.hybrid && this.motionReady && !!this.bestTier();
      if (motion) {
        // While the reader moves every change is the video's, so the picture
        // is one consistent thing. Once they have settled for a moment the
        // sharp frame for exactly this pose fades in over it.
        const settled = !moving && (performance.now() - this.stillSince) > 220;
        const exact = !!this.frames[index] && this.poseOfSharp(index) === pose;
        if (settled && exact) {
          this.showSharp(index);
          this.wantVideo = false;
          return;
        }
        // the sharp canvas shows another pose: it steps out only once the
        // motion canvas holds a picture (paintMotion does the rest)
        if (this.sharpShown && this.drawnIndex !== index && this.motionIndex >= 0) this.hideSharp();
        this.scheduleVideo(pose);
        if (settled && !this.frames[index]) this.ensure(index);
        return;
      }
      // No video yet: the sharp frames carry everything, nearest first.
      this.wantVideo = false;
      const use = this.nearestLoaded(index);
      if (use < 0) { if (this.active) this.ensure(index); return; }
      if (!force && use === this.drawnIndex && index === this.wantedIndex && this.sharpShown) return;
      this.wantedIndex = index;
      this.paints += 1;
      this.paintTimes.push(performance.now()); if (this.paintTimes.length > 240) this.paintTimes.shift();
      if (use !== index) this.substitutes += 1;
      const { ctx, canvas } = this;
      if (this.drawnIndex !== use || force) this.cover(ctx, this.frames[use], canvas.width, canvas.height);
      this.drawnIndex = use;
      if (!this.sharpShown) { this.root.classList.add('is-sharp'); this.sharpShown = true; }
      this.source = use === index ? 'sharp' : 'nearest';
      this.shown();
      if (this.progress) this.progress.style.setProperty('--progress', this.rendered.toFixed(3));
      if (this.rendered > 0.015) this.root.classList.add('is-moving');
      if (use !== index) this.ensure(index);
    }

    cover(ctx, img, w, h) {
      // a <video>, a VideoFrame or an ImageBitmap
      const iw = img.videoWidth || img.displayWidth || img.width;
      const ih = img.videoHeight || img.displayHeight || img.height;
      const s = Math.max(w / iw, h / ih);
      const dw = iw * s;
      const dh = ih * s;
      ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
    }
  }

  const boot = () => {
    document.querySelectorAll('[data-lautio-rack]').forEach((el) => {
      if (el.dataset.lautioBooted) return;
      el.dataset.lautioBooted = '1';
      const inst = new RackMotion(el);
      el.__inst = inst;          // the motion test reads rendered progress from here
      inst.init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme editor: sections are re-rendered, so boot any new instance
  document.addEventListener('shopify:section:load', boot);
})();
